import { useState } from "react"
import { t } from "translations"

import Button from "@mui/material/Button"
import Fab from "@mui/material/Fab"
import FormControlLabel from "@mui/material/FormControlLabel"
import MenuItem from "@mui/material/MenuItem"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import Select, { SelectChangeEvent } from "@mui/material/Select"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { nbNO } from "@mui/x-date-pickers/locales"

import { toLocalDate } from "@nhi/utils"

import Icon from "Components/Icon/Icon"
import Link from "Components/Link"

import calculateDueDateResult, {
    averageCycleLength,
    calculateTimeIntoPregnacy,
    calculateTimeLeftInPregnancy,
    printResult
} from "./utils"

interface IDueDateProps {
    dueDate: Date
    timeIntoPregnancy: number
    timeLeftToDueDate: number
    likelyConceptionDate: Date
}

export default function DueDateCalculator() {
    const [dueDateInfo, setDueDateInfo] = useState<IDueDateProps | undefined>(
        undefined
    )

    type RadioValue = "lastMenstration" | "dueDate"
    const [radioValue, setRadioValue] = useState<RadioValue>("lastMenstration")
    function handleRadioChange(event: React.ChangeEvent<HTMLInputElement>) {
        setRadioValue((event.target as HTMLInputElement).value as RadioValue)
    }

    type DateValue = Date | null
    const [dateChoosen, setDateChoosen] = useState<Date>(new Date())

    function handleDateChange(date: DateValue) {
        if (date) setDateChoosen(date)
    }

    const range = (start: number, stop: number, step: number) =>
        Array.from(
            { length: (stop - start) / step + 1 },
            (_, i) => start + i * step
        )

    const [cycleLength, setCycleLength] = useState<number>(averageCycleLength)
    // 24 - 38 is the range of cycle length
    const cycleOptions = range(24, 38, 1)

    const handleCycleChange = (event: SelectChangeEvent<unknown>) => {
        const {
            target: { value }
        } = event
        setCycleLength(Number(value))
    }

    function handleCalculate() {
        const result = calculateDueDateResult(
            radioValue,
            dateChoosen,
            cycleLength
        )
        if (result) {
            const {
                dueDate,
                totalDaysIntoPregnancy,
                totalDaysLeftInPregnancy,
                likelyFertilizationDate
            } = result
            setDueDateInfo({
                dueDate: dueDate,
                timeIntoPregnancy: totalDaysIntoPregnancy,
                timeLeftToDueDate: totalDaysLeftInPregnancy,
                likelyConceptionDate: likelyFertilizationDate
            })
        }
    }

    function handleResetAll() {
        setDateChoosen(new Date())
        setRadioValue("lastMenstration")
        setCycleLength(averageCycleLength)
        setDueDateInfo(undefined)
    }

    const disableShowFutureDates =
        radioValue === "lastMenstration" ? true : false

    return (
        <StyledWrapper>
            <StyledHeader>
                <Icon name={"pregnacyOracleStroller"} />
                <Typography variant="h2">
                    {t.dueDateCalculator.title}
                </Typography>
            </StyledHeader>
            <StyledContentWrapper>
                <StyledResetButton
                    size="small"
                    onClick={() => handleResetAll()}>
                    <Typography variant="body2">
                        {t.dueDateCalculator.reset}
                    </Typography>
                </StyledResetButton>

                <div>
                    <Typography variant="h6">
                        {t.dueDateCalculator.timeForCalculation}
                    </Typography>
                    <RadioGroup
                        aria-labelledby={t.dueDateCalculator.timeForCalculation}
                        name={t.dueDateCalculator.timeForCalculation}
                        value={radioValue}
                        onChange={handleRadioChange}>
                        <FormControlLabel
                            value="lastMenstration"
                            control={<StyledRadio />}
                            label={
                                <Typography variant="body2">
                                    {t.dueDateCalculator.firstDayOfLastPeriod}
                                </Typography>
                            }
                        />
                        <FormControlLabel
                            value="dueDate"
                            control={<StyledRadio />}
                            label={
                                <Typography variant="body2">
                                    {t.dueDateCalculator.dueDate}
                                </Typography>
                            }
                        />
                    </RadioGroup>
                </div>
                <div>
                    <Typography variant="h6">
                        {t.dueDateCalculator.chooseDate}
                    </Typography>
                    <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        localeText={
                            nbNO.components.MuiLocalizationProvider.defaultProps
                                .localeText
                        }>
                        <StyledDatePicker
                            disableFuture={disableShowFutureDates}
                            value={dateChoosen}
                            onChange={handleDateChange}
                        />
                    </LocalizationProvider>
                </div>
                <div>
                    <Typography variant="h6">
                        {t.dueDateCalculator.cycleLenghtInDays}
                    </Typography>
                    <StyledSelect
                        fullWidth
                        labelId={t.dueDateCalculator.cycleLenghtInDays}
                        value={cycleLength}
                        label={t.dueDateCalculator.cycleLenghtInDays}
                        onChange={handleCycleChange}>
                        {cycleOptions.map(cycleOption => (
                            <MenuItem
                                key={cycleOption}
                                value={cycleOption}>
                                {cycleOption}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                </div>

                <StyledButton
                    disableRipple
                    size="large"
                    startIcon={<Icon name={"start"} />}
                    onClick={handleCalculate}
                    variant="contained"
                    color="primary">
                    {t.dueDateCalculator.calculate}
                </StyledButton>

                {dueDateInfo && (
                    <StyledResult id="result">
                        <div>
                            <Typography variant="h6">
                                {t.dueDateCalculator.yourDueDate}
                            </Typography>
                            <Typography variant="subtitle2">
                                {`${toLocalDate(
                                    new Date(dueDateInfo.dueDate),
                                    process.env.NEXT_PUBLIC_LOCALE
                                )}`}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="h6">
                                {t.dueDateCalculator.pregnancyWeek}
                            </Typography>
                            <Typography variant="subtitle2">
                                {` ${calculateTimeIntoPregnacy(
                                    dueDateInfo.timeIntoPregnancy
                                )}`}
                            </Typography>
                        </div>

                        <div>
                            <Typography variant="h6">
                                {t.dueDateCalculator.timeLeft}
                            </Typography>
                            <Typography variant="subtitle2">
                                {` ${calculateTimeLeftInPregnancy(
                                    dueDateInfo.timeLeftToDueDate
                                )}`}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="h6">
                                {t.dueDateCalculator.propableConceptionDate}
                            </Typography>
                            <Typography variant="subtitle2">
                                {` ${toLocalDate(
                                    new Date(dueDateInfo.likelyConceptionDate),
                                    process.env.NEXT_PUBLIC_LOCALE
                                )}`}
                            </Typography>
                        </div>
                        <Tooltip
                            title={t.dueDateCalculator.printResult}
                            arrow
                            placement="left">
                            <StyledFab
                                size="small"
                                onClick={printResult}>
                                <Icon name="downloadOutlined" />
                            </StyledFab>
                        </Tooltip>
                    </StyledResult>
                )}
                <StyledLinkTypography variant="body2">
                    {t.pregnancyOracle.backTo}{" "}
                    <Link
                        href="/graviditetsorakelet/foster/0"
                        color="inherit"
                        fontStyle="inherit">
                        {t.pregnancyOracle.title}
                    </Link>
                </StyledLinkTypography>
            </StyledContentWrapper>
        </StyledWrapper>
    )
}

const StyledWrapper = styled("div")(({ theme }) => ({
    backgroundColor: theme.palette.pregnancy.light,
    display: "flex",
    width: "100%",
    margin: "auto",
    flexDirection: "column"
}))
const StyledResetButton = styled(Button)({
    margin: "auto",
    textDecoration: "underline 1px solid black",
    textUnderlineOffset: "0.2em",
    width: "fit-content"
})

const StyledHeader = styled("div")(({ theme }) => ({
    width: "100%",
    margin: "auto",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.pregnancy.main,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    "& .MuiSvgIcon-root": {
        fontSize: "2.4rem"
    },
    "& .MuiTypography-root": {
        color: theme.palette.common.black
    }
}))

const StyledContentWrapper = styled("div")(({ theme }) => ({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 2)
}))

const StyledRadio = styled(Radio)(({ theme }) => ({
    color: theme.palette.pregnancy.dark,
    "&.Mui-checked": {
        color: theme.palette.pregnancy.dark
    }
}))

const StyledSelect = styled(Select)(({ theme }) => ({
    backgroundColor: theme.palette.common.white,
    "& .MuiOutlinedInput-notchedOutline": { borderStyle: "none" },
    marginTop: theme.spacing(0.5)
}))

const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
    width: "100%",
    "& .MuiInputBase-root": {
        backgroundColor: theme.palette.common.white,
        "& .MuiOutlinedInput-notchedOutline": {
            border: "none"
        }
    }
}))

const StyledButton = styled(Button)(({ theme }) => ({
    "& .MuiButton-startIcon": {
        "& .MuiSvgIcon-root": {
            fontSize: "3rem"
        }
    },
    fontSize: "2.4rem",
    color: theme.palette.common.black,
    backgroundColor: theme.palette.pregnancy.dark,
    border: `2px solid ${theme.palette.pregnancy.main}`,
    "&:hover": {
        backgroundColor: theme.palette.pregnancy.dark,
        border: `2px solid ${theme.palette.pregnancy.dark}`
    },
    "&:focus": {
        outline: "none"
    }
}))

const StyledResult = styled("div")(({ theme }) => ({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    listStyle: "none",
    borderRadius: "2px",
    margin: theme.spacing(0),
    padding: theme.spacing(2),
    backgroundColor: "white"
}))

const StyledFab = styled(Fab)(({ theme }) => ({
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.pregnancy.main,
    marginLeft: "auto",
    boxShadow: "none",
    "&:hover": {
        backgroundColor: theme.palette.pregnancy.dark
    }
}))

const StyledLinkTypography = styled(Typography)({
    textAlign: "center",
    "& a": {
        textDecoration: "underline 1px solid black",
        textUnderlineOffset: "0.2em"
    }
})
